import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Cloud, ArrowClockwise, CheckCircle, Plus } from 'react-bootstrap-icons';
import AWSConnectionTest from './AWSConnectionTest';
import S3EmailStorage from './S3EmailStorage';
import EMMSectionHeader from './EMMSectionHeader';
import { useAccount } from '../contexts/AccountContext';
import { useTestAWSConnectionQuery, useGetDomainsQuery, useGetEmailAccountsQuery } from '../services/api';
import Onboarding from './Onboarding';

const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedAccount, isAllAccountsSelected, isLoading } = useAccount();
  
  // Check onboarding completion status
  const { data: awsConnection } = useTestAWSConnectionQuery();
  const { data: domainsData } = useGetDomainsQuery();
  const { data: emailAccountsData } = useGetEmailAccountsQuery();
  
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    activeCampaigns: 0,
    emailsSent: 0,
    openRate: 0
  });
  
  // Manual onboarding trigger only - no automatic display
  const [forceShowOnboarding, setForceShowOnboarding] = useState(false);

  useEffect(() => {
    // TODO: Fetch real stats from API based on selected account
    const env = process.env.REACT_APP_ENV;
    
    if (env === 'production') {
      // Production: Completely empty - new client experience
      setStats({
        totalSubscribers: 0,
        activeCampaigns: 0,
        emailsSent: 0,
        openRate: 0
      });
    } else {
      // Staging/Development: Show zeros until real data is loaded from API
      setStats({
        totalSubscribers: 0,
        activeCampaigns: 0,
        emailsSent: 0,
        openRate: 0
      });
    }
  }, [selectedAccount, isAllAccountsSelected]);

  // Only show onboarding if manually triggered via button
  if (forceShowOnboarding) {
    return (
      <Onboarding onForceHide={() => setForceShowOnboarding(false)} />
    );
  }

  return (
    <>
      <EMMSectionHeader title="Email Marketing Dashboard" showHelp />
      <Container className="mt-3" style={{ position: 'relative' }}>
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setForceShowOnboarding(true)}
          >
            Show Onboarding Steps
          </Button>
        </div>
      </div>
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Switching to {selectedAccount.alias}...</div>
          </div>
        </div>
      )}
      <Row>
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <p className="text-muted mb-0">Manage your email campaigns with Amazon SES</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Cloud className="me-2 text-primary" size={20} />
              <Badge bg={isAllAccountsSelected ? 'secondary' : 'primary'} className="fs-6">
                {selectedAccount.alias}
              </Badge>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.totalSubscribers.toLocaleString()}</h3>
              <p className="text-muted">Total Subscribers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.activeCampaigns}</h3>
              <p className="text-muted">Active Campaigns</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">{stats.emailsSent.toLocaleString()}</h3>
              <p className="text-muted">Emails Sent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{stats.openRate}%</h3>
              <p className="text-muted">Open Rate</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button as={Link} to="/campaigns/create" variant="primary">
                  Create New Campaign
                </Button>
                <Button as={Link} to="/templates" variant="outline-primary">
                  Manage Templates
                </Button>
                <Button as={Link} to="/subscribers" variant="outline-secondary">
                  View Subscribers
                </Button>
                <Button 
                  as={Link} 
                  to="/aws-accounts" 
                  variant="outline-success"
                  className="mt-2"
                >
                  <Plus className="me-2" />
                  Add AWS Account
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>Recent Activity</h5>
            </Card.Header>
            <Card.Body>
              <div className="list-group list-group-flush">
                {(() => {
                  // Calculate onboarding status - ACCOUNT SPECIFIC
                  const awsConnected = awsConnection?.data?.connected === true;
                  
                  // Get all domains and email accounts from API
                  let allDomains = domainsData?.data?.domains || [];
                  let allEmailAccounts = emailAccountsData?.data?.emailAccounts || [];
                  
                  // CRITICAL: Filter by selected account if not "all"
                  if (selectedAccount && selectedAccount.id && selectedAccount.id !== 'all') {
                    // Get the account's domains from AccountContext
                    const accountDomains = selectedAccount.domains || [];
                    const accountDomainNames = accountDomains.map(d => d.toLowerCase());
                    
                    // Filter domains by account's known domains
                    allDomains = allDomains.filter(domain => {
                      const domainName = domain.domain?.toLowerCase();
                      return accountDomainNames.some(accDomain => 
                        domainName === accDomain ||
                        domainName?.endsWith(`.${accDomain}`) ||
                        domainName?.includes(accDomain) ||
                        accDomain.includes(domainName)
                      );
                    });
                    
                    // Filter email accounts by account's domains
                    allEmailAccounts = allEmailAccounts.filter(emailAccount => {
                      const emailDomain = emailAccount.domain?.toLowerCase();
                      return accountDomainNames.some(accDomain => 
                        emailDomain === accDomain ||
                        emailDomain?.endsWith(`.${accDomain}`) ||
                        emailDomain?.includes(accDomain)
                      );
                    });
                  }
                  
                  const hasDomains = allDomains.length > 0;
                  const domainVerified = allDomains.some(domain => 
                    domain.spfStatus === 'configured' &&
                    domain.dkimStatus === 'configured' &&
                    domain.dmarcStatus === 'configured' &&
                    domain.verificationStatus === 'verified'
                  );
                  const hasEmailAccounts = allEmailAccounts.length > 0;
                  const hasWarmedAccounts = allEmailAccounts.some(account => 
                    account.status === 'warming' || 
                    account.status === 'active' ||
                    account.warmupStatus === 'in_progress' ||
                    account.warmupStatus === 'completed'
                  );
                  
                  // TODO: Check if campaigns exist for this account (when campaigns API is ready)
                  const hasCampaigns = false; // Placeholder - campaigns API not ready yet
                  
                  // Determine current step
                  let currentStep = null;
                  let isComplete = false;
                  
                  if (!awsConnected) {
                    currentStep = 'Connect to AWS Account';
                  } else if (!hasDomains) {
                    currentStep = 'Domain Name Setup';
                  } else if (!domainVerified) {
                    currentStep = 'Verify Your Domain';
                  } else if (!hasEmailAccounts) {
                    currentStep = 'Create Email Accounts';
                  } else if (!hasWarmedAccounts) {
                    currentStep = 'Warm Up Email Accounts';
                  } else if (!hasCampaigns) {
                    currentStep = 'Create Your First Campaign';
                    // NOT complete yet - campaigns required
                    isComplete = false;
                  } else {
                    // All steps complete including campaigns
                    currentStep = null; // No current step if all complete
                    isComplete = true;
                  }
                  
                  return (
                    <>
                      {isComplete ? (
                        <div className="list-group-item">
                          <div className="d-flex align-items-center">
                            <CheckCircle className="text-success me-2" size={20} />
                            <div>
                              <strong>Onboarding Completed</strong>
                              <div className="text-muted small">
                                {selectedAccount?.alias && selectedAccount.id !== 'all' 
                                  ? `All setup steps completed for ${selectedAccount.alias}`
                                  : 'All setup steps completed'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : currentStep ? (
                        <div className="list-group-item">
                          <div className="d-flex align-items-center">
                            <Cloud className="text-primary me-2" size={20} />
                            <div>
                              <strong>Current Step: {currentStep}</strong>
                              <div className="text-muted small">
                                Click "Show Onboarding Steps" to view detailed progress
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {/* TODO: Add other activity items from API */}
                      {(!currentStep && !isComplete) && (
                        <div className="text-center text-muted py-4">
                          <p>No recent activity</p>
                          <small>Activity will appear here once campaigns and actions are taken.</small>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* AWS Connection Test and S3 Email Storage - Side by Side */}
      <Row className="mt-4">
        <Col md={6}>
          <AWSConnectionTest />
        </Col>
        <Col md={6}>
          <S3EmailStorage />
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default Dashboard;

